import React, { useState } from 'react';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiEdit3, FiSend, FiStar } = FiIcons;

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
}

/**
 * Shows a single homework item: title, description, due date, status.
 * Submission form (text area); after submit shows feedback and star rating.
 */
export default function HomeworkCard({
  homework,
  submission = null,
  onSubmit,
  loading = false
}) {
  const [text, setText] = useState(submission?.submission_text ?? '');
  const [submitting, setSubmitting] = useState(false);

  const status = submission?.status ?? 'pending';
  const isGraded = status === 'graded';
  const isSubmitted = status === 'submitted' || isGraded;
  const canEdit = !isSubmitted;

  const handleSubmit = async () => {
    if (submitting || loading) return;
    setSubmitting(true);
    try {
      await onSubmit?.({
        submission_text: text,
        file_url: submission?.file_url
      });
    } finally {
      setSubmitting(false);
    }
  };

  const dueLabel = homework.due_date
    ? `Due ${formatDate(homework.due_date)}`
    : 'No due date';

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-2 mb-2">
        <h4 className="font-semibold text-slate-900">{homework.title}</h4>
        <span
          className={`text-xs font-medium px-2 py-1 rounded-full ${
            isGraded
              ? 'bg-emerald-100 text-emerald-800'
              : isSubmitted
                ? 'bg-blue-100 text-blue-800'
                : 'bg-slate-100 text-slate-600'
          }`}
        >
          {isGraded ? 'Graded' : isSubmitted ? 'Submitted' : 'Pending'}
        </span>
      </div>
      {homework.description && (
        <p className="text-sm text-slate-600 mb-2">{homework.description}</p>
      )}
      <p className="text-xs text-slate-500 mb-3">{dueLabel}</p>

      {canEdit && (
        <div className="space-y-2">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Your response..."
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm min-h-[100px]"
            rows={4}
          />
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || loading}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#00AAFF] text-white text-sm font-medium hover:opacity-90 disabled:opacity-50"
          >
            <SafeIcon icon={FiSend} className="w-4 h-4" />
            {submission?.id ? 'Update submission' : 'Submit'}
          </button>
        </div>
      )}

      {isGraded && (
        <div className="mt-3 pt-3 border-t border-slate-200">
          {submission.grade_stars != null && (
            <div className="flex items-center gap-1 text-amber-600 mb-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <SafeIcon
                  key={star}
                  icon={FiStar}
                  className={`w-5 h-5 ${star <= submission.grade_stars ? 'fill-current' : 'text-slate-300'}`}
                />
              ))}
              <span className="text-sm text-slate-600 ml-1">
                {submission.grade_stars}/5
              </span>
            </div>
          )}
          {submission.feedback_text && (
            <p className="text-sm text-slate-700">{submission.feedback_text}</p>
          )}
        </div>
      )}

      {isSubmitted && !isGraded && (
        <div className="mt-2">
          <p className="text-sm text-slate-600">Submitted — awaiting feedback.</p>
          {submission.submission_text && (
            <p className="text-sm text-slate-500 mt-1 italic">
              &ldquo;{submission.submission_text}&rdquo;
            </p>
          )}
        </div>
      )}
    </div>
  );
}
